import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Heading } from "./";
import TopMenuBar from "./TopMenuBar";
import FooterSection from "./FooterSection";
import LeadCaptureModal from "./LeadCaptureModal";

/**
 * Config-driven product landing page.
 *
 * Required config shape:
 * {
 *   slug, segment ("b2c" | "b2b"), eyebrow, title, tagline,
 *   description, stats: [{val, label}], statsNote?,
 *   primaryCta: {label, href, external?} | {label, action: "modal", modalKind: "waitlist"|"contact"},
 *   secondaryCta?: same shape as primaryCta,
 *   proof?: {
 *     eyebrow?, title?, body?,
 *     items?: [{label, value, detail?}],
 *     preview?: {eyebrow?, title, language?, code}
 *   },
 *   featuresHeading, featuresIntro, features: [{tag, title, description, bullets:[]}],
 *   useCasesHeading?, useCases?: [{icon, title, body, href?, external?}],
 *   ctaHeading, ctaBody, ctaButton: same shape as primaryCta
 * }
 */
export default function ProductLandingPage({ config }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState(null);

    if (!config) return null;

    const {
        segment,
        eyebrow,
        title,
        tagline,
        description,
        stats = [],
        statsNote,
        primaryCta,
        secondaryCta,
        proof,
        featuresHeading,
        featuresIntro,
        features = [],
        useCasesHeading,
        useCases = [],
        ctaHeading,
        ctaBody,
        ctaButton,
        seoDescription,
    } = config;

    const accent = segment === "b2c" ? "#ff8fb1" : "#7e60f8";
    const accent2 = segment === "b2c" ? "#7e60f8" : "#6ddcff";

    const openLeadModal = (cta) => {
        setModalConfig({ kind: cta.modalKind, context: `Interested in: ${title}` });
        setModalOpen(true);
    };

    const renderCta = (cta, primary = false) => {
        if (!cta) return null;
        const baseStyle = {
            fontFamily: "Manrope, sans-serif",
            fontSize: "0.95rem",
            fontWeight: 600,
            padding: "0.75rem 2rem",
            borderRadius: 12,
            textDecoration: "none",
            display: "inline-block",
            cursor: "pointer",
        };
        const style = primary
            ? { ...baseStyle, background: `linear-gradient(135deg, ${accent2}, ${accent})`, color: "#fff" }
            : { ...baseStyle, border: "1px solid #2a2a3a", color: "#e0e0e0" };

        if (cta.action === "modal") {
            return (
                <button type="button" style={{ ...style, border: primary ? "none" : style.border }} onClick={() => openLeadModal(cta)}>
                    {cta.label}
                </button>
            );
        }

        return cta.external ? (
            <a href={cta.href} target="_blank" rel="noopener noreferrer" style={style}>{cta.label}</a>
        ) : (
            <Link to={cta.href} style={style}>{cta.label}</Link>
        );
    };

    const renderProofPreview = (preview) => {
        if (!preview) return null;

        return (
            <div
                style={{
                    background: "linear-gradient(145deg, rgba(12,12,18,0.96), rgba(15,15,24,0.98))",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 18,
                    padding: "1.4rem",
                    textAlign: "left",
                    minHeight: "100%",
                }}
            >
                {preview.eyebrow && (
                    <div style={{
                        fontFamily: "Manrope, sans-serif",
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: accent,
                        marginBottom: "0.65rem",
                    }}>
                        {preview.eyebrow}
                    </div>
                )}
                <div style={{
                    fontFamily: "Manrope, sans-serif",
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: "#f4f4f6",
                    marginBottom: "0.85rem",
                }}>
                    {preview.title}
                </div>
                <pre
                    style={{
                        margin: 0,
                        padding: "1rem 1.1rem",
                        borderRadius: 14,
                        background: "rgba(0,0,0,0.34)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        color: "#d8def0",
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: "0.8rem",
                        lineHeight: 1.6,
                        fontFamily: "SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace",
                    }}
                >
                    {preview.code}
                </pre>
                {preview.language && (
                    <div style={{
                        fontFamily: "Manrope, sans-serif",
                        fontSize: "0.7rem",
                        color: "#7e8aa8",
                        marginTop: "0.75rem",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                    }}>
                        {preview.language}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Helmet>
                <title>{title} — AIHumanity</title>
                <meta name="description" content={seoDescription || description} />
            </Helmet>

            <div className="w-full bg-black-900_02">
                <TopMenuBar />

                {/* Hero */}
                <section className="relative flex flex-col items-center justify-center text-center px-5 pt-[120px] pb-[100px] overflow-hidden">
                    <svg
                        className="absolute opacity-[0.04] pointer-events-none"
                        style={{ top: -100, right: -100 }}
                        width="600" height="600" viewBox="0 0 600 600"
                    >
                        <circle cx="300" cy="300" r="260" fill="none" stroke={accent} strokeWidth="80" />
                        <circle cx="300" cy="300" r="160" fill="none" stroke={accent2} strokeWidth="30" />
                    </svg>

                    <span style={{
                        fontFamily: "Manrope, sans-serif",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        letterSpacing: "0.15em",
                        color: accent,
                        textTransform: "uppercase",
                        marginBottom: "1.25rem",
                        display: "block",
                    }}>
                        {eyebrow}
                    </span>

                    <Heading size="3xl" as="h1" className="!text-transparent bg-gradient5 bg-clip-text leading-tight max-w-[820px] mx-auto">
                        {tagline}
                    </Heading>

                    <p style={{
                        fontFamily: "Manrope, sans-serif",
                        fontSize: "1.2rem",
                        color: "#a0a0b8",
                        maxWidth: 640,
                        margin: "1.75rem auto 0",
                        lineHeight: 1.7,
                    }}>
                        {description}
                    </p>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "2.5rem", flexWrap: "wrap", justifyContent: "center" }}>
                        {renderCta(primaryCta, true)}
                        {renderCta(secondaryCta, false)}
                    </div>

                    {stats.length > 0 && (
                        <div style={{ display: "flex", gap: "3rem", marginTop: "4rem", flexWrap: "wrap", justifyContent: "center" }}>
                            {stats.map(({ val, label }) => (
                                <div key={label} style={{ textAlign: "center", fontFamily: "Manrope, sans-serif" }}>
                                    <div style={{
                                        fontSize: "1.6rem",
                                        fontWeight: 700,
                                        background: `linear-gradient(90deg, ${accent2}, ${accent})`,
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                    }}>{val}{statsNote ? " *" : ""}</div>
                                    <div style={{ fontSize: "0.8rem", color: "#666", marginTop: 4, letterSpacing: "0.05em" }}>{label}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {stats.length > 0 && statsNote && (
                        <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "#888", marginTop: "1rem", maxWidth: 480 }}>
                            * {statsNote}
                        </p>
                    )}
                </section>

                {/* Proof */}
                {proof && (
                    <section className="px-5 pb-[96px]">
                        <div
                            className="mx-auto"
                            style={{
                                maxWidth: 1120,
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                                gap: "1.5rem",
                                alignItems: "stretch",
                            }}
                        >
                            <div
                                style={{
                                    background: "linear-gradient(145deg, rgba(13,13,20,0.96), rgba(18,18,28,0.98))",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    borderRadius: 20,
                                    padding: "2rem",
                                    textAlign: "left",
                                }}
                            >
                                {proof.eyebrow && (
                                    <div style={{
                                        fontFamily: "Manrope, sans-serif",
                                        fontSize: "0.72rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.12em",
                                        textTransform: "uppercase",
                                        color: accent,
                                        marginBottom: "0.8rem",
                                    }}>
                                        {proof.eyebrow}
                                    </div>
                                )}
                                {proof.title && (
                                    <Heading size="lg" as="h2" className="mb-3">
                                        {proof.title}
                                    </Heading>
                                )}
                                {proof.body && (
                                    <p style={{
                                        fontFamily: "Manrope, sans-serif",
                                        fontSize: "0.96rem",
                                        color: "#9fa7be",
                                        lineHeight: 1.7,
                                        marginBottom: proof.items?.length ? "1.5rem" : 0,
                                    }}>
                                        {proof.body}
                                    </p>
                                )}

                                {proof.items?.length > 0 && (
                                    <div style={{ display: "grid", gap: "0.9rem" }}>
                                        {proof.items.map((item) => (
                                            <div
                                                key={`${item.label}-${item.value}`}
                                                style={{
                                                    padding: "1rem 1rem 0.95rem",
                                                    borderRadius: 14,
                                                    background: "rgba(255,255,255,0.025)",
                                                    border: "1px solid rgba(255,255,255,0.06)",
                                                }}
                                            >
                                                <div style={{
                                                    fontFamily: "Manrope, sans-serif",
                                                    fontSize: "0.72rem",
                                                    fontWeight: 700,
                                                    letterSpacing: "0.08em",
                                                    textTransform: "uppercase",
                                                    color: "#7381a6",
                                                    marginBottom: "0.35rem",
                                                }}>
                                                    {item.label}
                                                </div>
                                                <div style={{
                                                    fontFamily: "Manrope, sans-serif",
                                                    fontSize: "1.05rem",
                                                    fontWeight: 700,
                                                    color: "#f4f4f6",
                                                    marginBottom: item.detail ? "0.35rem" : 0,
                                                }}>
                                                    {item.value}
                                                </div>
                                                {item.detail && (
                                                    <div style={{
                                                        fontFamily: "Manrope, sans-serif",
                                                        fontSize: "0.86rem",
                                                        color: "#98a2bf",
                                                        lineHeight: 1.6,
                                                    }}>
                                                        {item.detail}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {renderProofPreview(proof.preview)}
                        </div>
                    </section>
                )}

                {/* Features */}
                {features.length > 0 && (
                    <section className="px-5 pb-[100px] max-w-[1040px] mx-auto">
                        <div style={{ textAlign: "center", marginBottom: "3.5rem" }}>
                            <Heading size="xl" as="h2" className="!text-transparent bg-gradient5 bg-clip-text">
                                {featuresHeading}
                            </Heading>
                            {featuresIntro && (
                                <p style={{ fontFamily: "Manrope, sans-serif", color: "#888", marginTop: "0.75rem", fontSize: "1rem" }}>
                                    {featuresIntro}
                                </p>
                            )}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.5rem" }}>
                            {features.map((f) => (
                                <div
                                    key={f.title}
                                    style={{
                                        background: "linear-gradient(145deg, #0d0d14, #11111e)",
                                        border: "1px solid #1e1e2e",
                                        borderRadius: 16,
                                        padding: "2rem",
                                        position: "relative",
                                        overflow: "hidden",
                                    }}
                                >
                                    <div style={{
                                        position: "absolute",
                                        top: 0, left: "2rem", right: "2rem",
                                        height: 2,
                                        background: `linear-gradient(90deg, ${accent2}, ${accent})`,
                                        borderRadius: 1,
                                    }} />

                                    {f.tag && (
                                        <span style={{
                                            fontFamily: "Manrope, sans-serif",
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            color: accent,
                                            letterSpacing: "0.12em",
                                            textTransform: "uppercase",
                                        }}>
                                            {f.tag}
                                        </span>
                                    )}

                                    <Heading size="md" as="h3" className="mt-2 mb-3">
                                        {f.title}
                                    </Heading>

                                    <p style={{
                                        fontFamily: "Manrope, sans-serif",
                                        fontSize: "0.9rem",
                                        color: "#a0a0b8",
                                        lineHeight: 1.7,
                                        marginBottom: f.bullets?.length ? "1.25rem" : 0,
                                    }}>
                                        {f.description}
                                    </p>

                                    {f.bullets?.length > 0 && (
                                        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                            {f.bullets.map((b) => (
                                                <li key={b} style={{
                                                    fontFamily: "Manrope, sans-serif",
                                                    fontSize: "0.85rem",
                                                    color: "#c0c0d0",
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: "0.5rem",
                                                    marginBottom: "0.4rem",
                                                }}>
                                                    <span style={{ color: accent2, marginTop: 2, flexShrink: 0 }}>›</span>
                                                    {b}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Use cases */}
                {useCases.length > 0 && (
                    <section style={{ background: "linear-gradient(180deg, #0a0a12, #0d0d14)", padding: "80px 20px" }}>
                        <div className="max-w-[1040px] mx-auto">
                            <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                                <Heading size="xl" as="h2" className="!text-transparent bg-gradient5 bg-clip-text">
                                    {useCasesHeading || "Where it fits"}
                                </Heading>
                            </div>
                            <div style={
                                useCases.length === 1
                                    ? { display: "flex", justifyContent: "center" }
                                    : { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1.25rem" }
                            }>
                                {useCases.map((u) => {
                                    const cardStyle = {
                                        background: "#0d0d18",
                                        border: "1px solid #1e1e2e",
                                        borderRadius: 14,
                                        padding: "1.75rem",
                                        display: "block",
                                        textDecoration: "none",
                                        ...(useCases.length === 1 ? { width: "100%", maxWidth: 320 } : {}),
                                    };
                                    const cardContent = (
                                        <>
                                            <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>{u.icon}</div>
                                            <Heading size="s" as="h4" className="mb-2">{u.title}</Heading>
                                            <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.875rem", color: "#888", lineHeight: 1.65 }}>{u.body}</p>
                                        </>
                                    );
                                    if (!u.href) {
                                        return <div key={u.title} style={cardStyle}>{cardContent}</div>;
                                    }
                                    return u.external ? (
                                        <a key={u.title} href={u.href} target="_blank" rel="noopener noreferrer" style={cardStyle}>{cardContent}</a>
                                    ) : (
                                        <Link key={u.title} to={u.href} style={cardStyle}>{cardContent}</Link>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}

                {/* CTA */}
                <section style={{ textAlign: "center", padding: "80px 20px", borderTop: "1px solid #1e1e2e" }}>
                    <Heading size="2xl" as="h2" className="!text-transparent bg-gradient5 bg-clip-text max-w-[600px] mx-auto mb-6">
                        {ctaHeading}
                    </Heading>
                    {ctaBody && (
                        <p style={{ fontFamily: "Manrope, sans-serif", color: "#888", fontSize: "1rem", marginBottom: "2rem" }}>
                            {ctaBody}
                        </p>
                    )}
                    {ctaButton && (() => {
                        const bigStyle = {
                            fontFamily: "Manrope, sans-serif",
                            fontSize: "1rem",
                            fontWeight: 600,
                            padding: "0.9rem 2.5rem",
                            borderRadius: 14,
                            background: `linear-gradient(135deg, ${accent2}, ${accent})`,
                            color: "#fff",
                            textDecoration: "none",
                            display: "inline-block",
                            border: "none",
                            cursor: "pointer",
                        };

                        if (ctaButton.action === "modal") {
                            return (
                                <button type="button" style={bigStyle} onClick={() => openLeadModal(ctaButton)}>
                                    {ctaButton.label} →
                                </button>
                            );
                        }

                        return ctaButton.external ? (
                            <a href={ctaButton.href} target="_blank" rel="noopener noreferrer" style={bigStyle}>
                                {ctaButton.label} →
                            </a>
                        ) : (
                            <Link to={ctaButton.href} style={bigStyle}>
                                {ctaButton.label} →
                            </Link>
                        );
                    })()}
                </section>

                <FooterSection />
            </div>

            <LeadCaptureModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                kind={modalConfig?.kind}
                context={modalConfig?.context}
            />
        </>
    );
}
